import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, FormControl, FormLabel, Input, useToast, Modal,
    ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
    Flex, Text, RadioGroup, Radio, HStack, Icon,
    IconButton,
    Checkbox,
    Spinner
} from "@chakra-ui/react";
import { CardElement, ElementsConsumer, useStripe } from "@stripe/react-stripe-js";
import { FaCreditCard, FaApple, FaBitcoin, FaTimes, FaGoogle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/axios';
import { useAuth } from '../../../hooks/useAuth';
import { useSubscription } from '../../../context/SubscriptionContext';
import CustomSelect from '../../../utils/CustomSelect';
import PaymentRequestButton from '../../Payments/PaymentRequestButton';





const PaymentMethodSelect = ({ selectedPaymentMethod, setSelectedPaymentMethod, applePaySupported, googlePaySupported, supportCheckLoading }) => (
    <FormControl w="100%" mt={4} backgroundColor="secondary.900" p={3}>
        <FormLabel fontSize="lg" fontWeight="bold">Payment Method</FormLabel>
        <RadioGroup onChange={setSelectedPaymentMethod} value={selectedPaymentMethod}>
            <HStack spacing={8}>
                <Box display="flex" alignItems="center" cursor="pointer">
                    <Radio value="card">
                        <HStack>
                            <Icon as={FaCreditCard} boxSize={8} />
                            <Text>Card</Text>
                        </HStack>
                    </Radio>
                </Box>
                <Box display="flex" alignItems="center" cursor="pointer">
                    <Radio value="applePay" isDisabled={!applePaySupported && !supportCheckLoading}>
                        <HStack>
                            <Icon as={FaApple} boxSize={8} color={!applePaySupported && !supportCheckLoading ? "gray.500" : "inherit"} />
                            <Text color={!applePaySupported && !supportCheckLoading ? "gray.500" : "inherit"}>Apple Pay</Text>
                        </HStack>
                    </Radio>
                </Box>
                <Box display="flex" alignItems="center" cursor="pointer">
                    <Radio value="googlePay" isDisabled={!googlePaySupported && !supportCheckLoading}>
                        <HStack>
                            <Icon as={FaGoogle} boxSize={8} color={!googlePaySupported && !supportCheckLoading ? "gray.500" : "inherit"} />
                            <Text color={!googlePaySupported && !supportCheckLoading ? "gray.500" : "inherit"}>Google Pay</Text>
                        </HStack>
                    </Radio>
                </Box>
                <Box display="flex" alignItems="center" cursor="pointer">
                    <Radio value="cryptoPay" isDisabled={true}>
                        <HStack>
                            <Icon as={FaBitcoin} boxSize={8} color="gray.500" />
                            <Text color="gray.500">Crypto Pay</Text>
                        </HStack>
                    </Radio>
                </Box>
            </HStack>
        </RadioGroup>
    </FormControl>
);

const PaymentModal = ({ isOpen, onClose, onPaymentSuccess, trackDetails }) => {
    const toast = useToast();
    const stripe = useStripe();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [discountCode, setDiscountCode] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [currentPrice] = useState(trackDetails?.price_per_share || 0);
    const [newPrice, setNewPrice] = useState(currentPrice)
    const [priceChangeFee, setPriceChangeFee] = useState(7);
    const [totalPrice, setTotalPrice] = useState(priceChangeFee)
    const [showDiscount, setShowDiscount] = useState(false)
    const [paymentMethodSelected, setPaymentMethodSelected] = useState('')
    const [isAddingNewMethod, setIsAddingNewMethod] = useState(false);
    const [savePaymentMethod, setSavePaymentMethod] = useState(true); // Toggle for saving payment method
    
    // Payment method support detection
    const [applePaySupported, setApplePaySupported] = useState(false);
    const [googlePaySupported, setGooglePaySupported] = useState(false);
    const [supportCheckLoading, setSupportCheckLoading] = useState(false);


    const { paymentMethods, getPaymentMethods, createPaymentIntent, createPaymentMethod, createPaymentIntentWithoutSaving } = useSubscription()


    // Function to check if Apple Pay and Google Pay are supported
    const checkPaymentMethodSupport = useCallback(async () => {
        if (!stripe) return;

        setSupportCheckLoading(true);
        try {
            const paymentRequest = stripe.paymentRequest({
                country: 'US',
                currency: 'usd',
                total: {
                    label: 'Test',
                    amount: 100, // $1.00 for testing
                },
                requestPayerName: true,
                requestPayerEmail: true,
            });

            const canMakePayment = await paymentRequest.canMakePayment();
            if (canMakePayment) {
                // Check which specific methods are available
                setApplePaySupported(canMakePayment.applePay || false);
                setGooglePaySupported(canMakePayment.googlePay || false);
            } else {
                setApplePaySupported(false);
                setGooglePaySupported(false);
            }
        } catch (error) {
            console.error('Error checking payment method support:', error);
            setApplePaySupported(false);
            setGooglePaySupported(false);
        } finally {
            setSupportCheckLoading(false);
        }
    }, [stripe]);

    // Check support when stripe is loaded
    useEffect(() => {
        if (stripe) {
            checkPaymentMethodSupport();
        }
    }, [stripe, checkPaymentMethodSupport]);

    useEffect(() => {
        // Fetch available payment methods on component load
        const fetchPaymentMethods = async () => {
            try {
                await getPaymentMethods();
            } catch (error) {
                toast({
                    title: 'Error fetching payment methods',
                    description: error.message || 'Please try again later.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        fetchPaymentMethods();
    }, [getPaymentMethods, toast]);




    const CARD_OPTIONS = {
        style: {
            base: {
                fontSize: "16px",
                color: "#424770", // ✅ Your requested text color
                fontFamily: "Poppins, sans-serif",
                "::placeholder": { color: "#aab7c4" }, // ✅ Your requested placeholder color
                padding: "12px",
                backgroundColor: "transparent",
                borderRadius: "6px",
            },
            invalid: {
                color: "#ff4c4c", // Error text color
                iconColor: "#ff4c4c",
            },
            complete: {
                color: "#4caf50", // Success color
            },
        },
        hidePostalCode: true, // Hide postal code if not needed
        disableLink: true,
    };

    const handleApplyDiscount = async () => {

        try {
            const response = await axiosInstance.get(`/coupons/get_coupon_by_code/${discountCode}`);
            const { discount } = response.data;

            let finalDiscout = discount <= priceChangeFee ? discount : priceChangeFee

            setDiscountAmount(finalDiscout);
            toast({
                title: "Discount applied",
                description: `Discount of $${finalDiscout} applied successfully!`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            setTotalPrice(priceChangeFee - finalDiscout)
            setShowDiscount(true)
        } catch (error) {
            toast({
                title: "Invalid coupon",
                description: "The coupon code is invalid or expired.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };
    const completeOrder = async () => {

        let params = {
            cart_items: {
                items: [trackDetails]
            },
            total_price: totalPrice * 100,
            payment_method: selectedPaymentMethod,
            buyer_id: user.user_id,
            coupon_code: discountCode || "None",
            new_price: parseFloat(newPrice)
        }
        console.log(params)

        try {
            const res = await axiosInstance.post("/music/complete_track_price_change", params);
            console.log(res.data);

            toast({
                title: "Price Changed successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            onPaymentSuccess()
            navigate("/dashboard/artist/tracks");
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to complete order. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handlePayment = async (data, elements, stripe) => {
        setLoading(true);
        try {
            let paymentMethodId = null;

            if (paymentMethodSelected !== 'new' && paymentMethodSelected !== '') {
                paymentMethodId = paymentMethodSelected
            }
            else {

                if (selectedPaymentMethod === 'card') {
                    const cardElement = elements.getElement(CardElement);
                    if (!cardElement) {
                        toast({
                            title: 'Error',
                            description: 'Please enter valid card details',
                            status: 'error',
                            duration: 3000,
                            isClosable: true,
                        });
                        return;
                    }
                    const billingDetails = {
                        name: data.cardName,
                        address: {
                            line1: data.billingAddress,
                            city: data.city,
                            state: data.state,
                            postal_code: data.postalCode,
                            country: 'US',
                        },
                    };

                    paymentMethodId = await createPaymentMethod(cardElement, stripe, billingDetails);
                } else if (selectedPaymentMethod === 'applePay' || selectedPaymentMethod === 'googlePay') {
                    toast({
                        title: 'Use the Payment Button Above',
                        description: 'Please use the Apple Pay or Google Pay button above instead of the radio selection.',
                        status: 'info',
                        duration: 5000,
                        isClosable: true,
                    });
                    return;
                }
            }

            if (!paymentMethodId) {
                toast({
                    title: 'Error',
                    description: 'Please select a payment method or pay using new one',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const amount = Math.round(totalPrice * 100); // Amount in cents with proper rounding
            let paymentIntent = null
            if (savePaymentMethod) {
                paymentIntent = await createPaymentIntent(amount, 'usd', paymentMethodId, discountCode);
            } else {
                paymentIntent = await createPaymentIntentWithoutSaving(amount, "usd", paymentMethodId, discountCode);
            }
            if (paymentIntent) {
                await completeOrder();
            }

        } catch (error) {
            toast({
                title: 'Payment Error',
                description: error.message || 'Something went wrong. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Payment</ModalHeader>
                <ModalCloseButton />
                <ModalBody>

                    <Flex my={3} flexDirection="column" bg="secondary.100" color="dark.900" justifyContent="center" p={3} >

                        <Flex justifyContent="space-between">

                            <h2>Current price</h2>
                            <h2>${trackDetails.price_per_share}</h2>
                        </Flex>


                    </Flex>
                    <FormControl mt={4}>
                        <FormLabel>New price</FormLabel>
                        <Input
                            type="text"
                            value={newPrice}
                            onChange={(e) => setNewPrice(e.target.value)}
                            placeholder="Enter New Price"
                            variant="light"
                            color="secondary.100"
                        />

                    </FormControl>
                    <Flex my={3} flexDirection="column" bg="secondary.100" color="dark.900" justifyContent="center" p={3} >
                        <Flex justifyContent="space-between" color="primary.900">

                            <h2>Price Change Fee</h2>
                            <h2>${priceChangeFee}</h2>
                        </Flex>

                        <Flex justifyContent="space-between" alignItems="center" paddingY={3} display={showDiscount ? "flex" : "none"}>

                            <Text> Applied <Text as="span" mx={3} color="green.400">{discountCode}</Text>  <IconButton
                                variant="solid"
                                size={"sm"}
                                color="red.500"
                                icon={<FaTimes />}
                                onClick={() => {
                                    setDiscountAmount(0);
                                    setPriceChangeFee(priceChangeFee)
                                    setShowDiscount(false)
                                }}
                            /> </Text>
                            <h2>- ${discountAmount}</h2>


                        </Flex>
                        <Flex justifyContent="space-between">

                            <h2>Total Price</h2>
                            <h2>${totalPrice}</h2>
                        </Flex>

                    </Flex>

                    <FormControl mt={4}>
                        <FormLabel>Discount Code</FormLabel>
                        <Input
                            type="text"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                            placeholder="Enter discount code"
                            variant="light"
                            color="secondary.100"
                        />

                        <Button mt={2} onClick={handleApplyDiscount}>
                            Apply Discount
                        </Button>
                    </FormControl>

                    <ElementsConsumer>

                        {({ elements, stripe }) => (
                            <Box as="form" onSubmit={(e) => { e.preventDefault(); handlePayment({}, elements, stripe); }}>
                                <FormControl>
                                    <FormLabel
                                        mt={6}
                                        fontSize={{ base: 'sm', md: 'md' }}
                                        color="dark.100"
                                    >
                                        Select Payment Method
                                    </FormLabel>

                                    <CustomSelect
                                        placeholder="Choose payment method"
                                        value={paymentMethodSelected}
                                        onChange={(val) => {
                                            setPaymentMethodSelected(val);
                                            setIsAddingNewMethod(val === 'new');
                                        }}
                                        width="100%"
                                    >
                                        {paymentMethods.map((method) => (
                                            <option key={method.id} value={method.id}>
                                                {method.card?.brand.toUpperCase()} ****{method.card?.last4}
                                            </option>
                                        ))}
                                        <option value="new">Add New Payment Method</option>
                                    </CustomSelect>
                                </FormControl>

                                {/* Apple Pay and Google Pay Button */}
                                <Box mt={4}>
                                    <PaymentRequestButton
                                        amount={Math.round(totalPrice * 100)} // Convert to cents with proper rounding
                                        currency="usd"
                                        country="US"
                                        label={`Price change fee - $${totalPrice}`}
                                        couponCode={discountCode}
                                        onPaymentSuccess={(paymentIntent, breakdown) => {
                                            console.log('Payment successful:', paymentIntent, breakdown);
                                            completeOrder();
                                        }}
                                        onPaymentError={(error) => {
                                            console.error('Payment error:', error);
                                            toast({
                                                title: "Payment Error",
                                                description: error.message || "Payment failed. Please try again.",
                                                status: "error",
                                                duration: 5000,
                                                isClosable: true,
                                            });
                                        }}
                                    />
                                </Box>

                                {/* Divider between Apple Pay/Google Pay and Card payment */}
                                <Box textAlign="center" my={4} position="relative">
                                    <Text
                                        bg="white"
                                        px={3}
                                        color="gray.500"
                                        fontSize="sm"
                                        position="relative"
                                        zIndex={1}
                                    >
                                        OR
                                    </Text>
                                    <Box
                                        position="absolute"
                                        top="50%"
                                        left={0}
                                        right={0}
                                        height="1px"
                                        bg="gray.300"
                                        zIndex={0}
                                    />
                                </Box>

                                {isAddingNewMethod && (
                                    <>
                                        <PaymentMethodSelect
                                            selectedPaymentMethod={selectedPaymentMethod}
                                            setSelectedPaymentMethod={setSelectedPaymentMethod}
                                            applePaySupported={applePaySupported}
                                            googlePaySupported={googlePaySupported}
                                            supportCheckLoading={supportCheckLoading}
                                        />
                                        
                                        {/* Support Status Messages */}
                                        {selectedPaymentMethod === 'applePay' && !applePaySupported && !supportCheckLoading && (
                                            <Box mt={4} p={3} bg="red.100" borderRadius="md" border="1px solid" borderColor="red.300">
                                                <Text color="red.700" fontSize="sm" fontWeight="bold">
                                                    ⚠️ Apple Pay is not supported
                                                </Text>
                                                <Text color="red.600" fontSize="xs" mt={1}>
                                                    Apple Pay requires Safari browser with saved payment methods on a supported device.
                                                </Text>
                                            </Box>
                                        )}

                                        {selectedPaymentMethod === 'googlePay' && !googlePaySupported && !supportCheckLoading && (
                                            <Box mt={4} p={3} bg="red.100" borderRadius="md" border="1px solid" borderColor="red.300">
                                                <Text color="red.700" fontSize="sm" fontWeight="bold">
                                                    ⚠️ Google Pay is not supported
                                                </Text>
                                                <Text color="red.600" fontSize="xs" mt={1}>
                                                    Google Pay requires Chrome browser with saved payment methods.
                                                </Text>
                                            </Box>
                                        )}

                                        {selectedPaymentMethod === 'cryptoPay' && (
                                            <Box mt={4} p={3} bg="gray.100" borderRadius="md" border="1px solid" borderColor="gray.300">
                                                <Text color="gray.700" fontSize="sm" fontWeight="bold">
                                                    🚧 Crypto Pay is not implemented
                                                </Text>
                                                <Text color="gray.600" fontSize="xs" mt={1}>
                                                    Cryptocurrency payments are coming soon.
                                                </Text>
                                            </Box>
                                        )}

                                        {supportCheckLoading && (selectedPaymentMethod === 'applePay' || selectedPaymentMethod === 'googlePay') && (
                                            <Box mt={4} p={3} bg="blue.100" borderRadius="md" border="1px solid" borderColor="blue.300">
                                                <Flex align="center">
                                                    <Spinner size="sm" color="blue.500" mr={2} />
                                                    <Text color="blue.700" fontSize="sm">
                                                        Checking payment method support...
                                                    </Text>
                                                </Flex>
                                            </Box>
                                        )}
                                        {selectedPaymentMethod === 'card' && (
                                            <>
                                                <FormControl mt={4}>
                                                    <FormLabel>Name on Card</FormLabel>
                                                    <Input type="text" name="cardName" variant="light" />
                                                </FormControl>
                                                <FormControl mt={4}>
                                                    <FormLabel>Billing Address</FormLabel>
                                                    <Input type="text" name="billingAddress" variant="light" />
                                                </FormControl>
                                                <Flex direction="row" mt={4}>
                                                    <FormControl>
                                                        <FormLabel>City</FormLabel>
                                                        <Input type="text" name="city" variant="light" />
                                                    </FormControl>
                                                    <FormControl ml={4}>
                                                        <FormLabel>State</FormLabel>
                                                        <Input type="text" name="state" variant="light" />
                                                    </FormControl>
                                                </Flex>
                                                <FormControl mt={4}>
                                                    <FormLabel>ZIP/Postal Code</FormLabel>
                                                    <Input type="text" name="postalCode" variant="light" />
                                                </FormControl>
                                                <FormControl mt={4}>
                                                    <FormLabel>Card Details</FormLabel>
                                                    <Box
                                                        p={4}
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        bg="white"
                                                        _focus={{ borderColor: "primary.500", boxShadow: "0px 0px 8px rgba(72, 187, 120, 0.8)" }}
                                                    >
                                                        <CardElement options={CARD_OPTIONS} />
                                                    </Box>
                                                </FormControl>
                                            </>
                                        )}

                                        <FormControl mt={4}>
                                            <Checkbox
                                                isChecked={savePaymentMethod}
                                                onChange={(e) => setSavePaymentMethod(e.target.checked)}
                                            >
                                                Save this payment method for future use
                                            </Checkbox>
                                        </FormControl>

                                    </>)}

                                <Button 
                                    mt={4} 
                                    colorScheme="teal" 
                                    isLoading={loading} 
                                    type="submit" 
                                    w="100%"
                                    isDisabled={
                                        loading ||
                                        (selectedPaymentMethod === 'applePay' && !applePaySupported) ||
                                        (selectedPaymentMethod === 'googlePay' && !googlePaySupported) ||
                                        selectedPaymentMethod === 'cryptoPay' ||
                                        supportCheckLoading
                                    }
                                >
                                    {isAddingNewMethod && savePaymentMethod ? 'Save and Pay' : 'Pay'} ${totalPrice}
                                </Button>

                            </Box>
                        )}
                    </ElementsConsumer>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default PaymentModal;
